import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export type PaletteItem = { path: string; label: string; group: string | null };

/**
 * Keyboard navigation between destinations.
 *
 * macOS would normally do this with Command-1..9, the way Finder and Safari
 * switch views — but those are browser-reserved and a page cannot intercept
 * them, so the shortcut is Command-K. The interaction itself is AppKit's:
 * arrow keys move a highlighted selection, Return opens it, Escape dismisses,
 * and the panel is a material sheet rather than a flat overlay.
 */
export function CommandPalette({ items }: { items: PaletteItem[] }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
        setTerm("");
        setActive(0);
      }
    };
    // The top bar's search field opens the same palette, so there is one
    // search, reachable by keyboard and by pointer alike.
    const onOpen = () => { setOpen(true); setTerm(""); setActive(0); };
    document.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onOpen);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const hits = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.label.toLowerCase().includes(q)
      || (i.group ?? "").toLowerCase().includes(q));
  }, [items, term]);

  useEffect(() => { setActive(0); }, [term]);

  if (!open) return null;

  const go = (index: number) => {
    const hit = hits[index];
    if (!hit) return;
    setOpen(false);
    navigate(hit.path);
  };

  return (
    <div
      role="presentation"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(0, 0, 0, .28)",
        display: "grid", justifyItems: "center", alignItems: "start",
        padding: "12vh var(--ui-space-4) var(--ui-space-4)",
      }}
    >
      <div
        className="ui-enter"
        role="dialog"
        aria-modal="true"
        aria-label="Go to"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 100%)",
          background: "var(--c-material-sheet)",
          backdropFilter: "var(--ui-material)",
          WebkitBackdropFilter: "var(--ui-material)",
          border: "1px solid var(--c-border)",
          borderRadius: "var(--ui-sheet-radius)",
          boxShadow: "var(--ui-shadow-sheet)",
          overflow: "hidden",
        }}
      >
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Go to…"
          aria-label="Go to"
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
            else if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((n) => Math.min(n + 1, hits.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((n) => Math.max(n - 1, 0));
            } else if (e.key === "Enter") { e.preventDefault(); go(active); }
          }}
          style={{
            width: "100%", height: 44, padding: "0 14px",
            border: "none", borderBottom: "1px solid var(--c-border)",
            background: "transparent", color: "var(--c-t1)",
            font: "inherit", fontSize: "var(--ui-text-body)", outline: "none",
          }}
        />
        <div ref={listRef} role="listbox" aria-label="Destinations" style={{ maxHeight: 320, overflowY: "auto", padding: 4 }}>
          {hits.length === 0 && (
            <div style={{
              padding: "10px 12px", fontSize: "var(--ui-text-control)", color: "var(--c-t3)",
            }}>No destination matches “{term}”.</div>
          )}
          {hits.map((hit, i) => (
            <div
              key={hit.path}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(i)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "7px 10px", borderRadius: "var(--ui-control-radius)",
                cursor: "pointer", fontSize: "var(--ui-text-control)",
                background: i === active ? "var(--c-selection)" : "transparent",
                color: i === active ? "var(--c-accent-on)" : "var(--c-t2)",
              }}
            >
              <span>{hit.label}</span>
              {hit.group && (
                <span style={{
                  fontSize: "var(--ui-text-meta)",
                  color: i === active ? "var(--c-accent-on)" : "var(--c-t4)",
                  opacity: i === active ? .8 : 1,
                }}>{hit.group}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
